/**
 * Optimistic Update Helpers for React Query
 *
 * This module provides reusable utilities to implement optimistic updates
 * with automatic rollback on error. These helpers follow React Query best
 * practices and eliminate boilerplate in mutation hooks.
 *
 * Key features:
 * - Automatic snapshot creation for rollback
 * - Temporary IDs for optimistic items
 * - Type-safe generic implementations
 * - Consistent error handling pattern
 */

import type { QueryClient } from '@tanstack/react-query';

/**
 * Base interface for items with an ID
 * All entities must have at least an id field
 */
interface BaseEntity {
  id: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

/**
 * Context returned by onMutate for rollback
 */
interface MutationContext<T> {
  previousData?: T;
}

/**
 * Creates a temporary ID for optimistic items
 * Format: temp-{timestamp}-{random}
 *
 * This ensures uniqueness even for rapid successive mutations
 */
export const createTempId = (): string => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `temp-${timestamp}-${random}`;
};

/**
 * Creates an optimistic item with temporary ID and timestamps
 *
 * @param item - Partial item data from the user
 * @returns Complete item with temp ID and timestamps
 */
export const createOptimisticItem = <T extends BaseEntity>(
  item: Partial<T>
): T => {
  const now = new Date().toISOString();
  return {
    ...item,
    id: createTempId(),
    createdAt: now,
    updatedAt: now,
  } as T;
};

/**
 * Replaces optimistic items (with temp IDs) with real server data
 *
 * @param items - Current array of items
 * @param newItem - Real item from server
 * @returns Updated array with optimistic item replaced
 */
export const replaceOptimisticItem = <T extends BaseEntity>(
  items: T[],
  newItem: T
): T[] => {
  return items.map((item) => (item.id.startsWith('temp-') ? newItem : item));
};

/**
 * Removes an item from the list (for delete operations)
 *
 * @param items - Current array of items
 * @param id - ID of item to remove
 * @returns Updated array without the item
 */
export const removeItem = <T extends BaseEntity>(
  items: T[],
  id: string
): T[] => {
  return items.filter((item) => item.id !== id);
};

/**
 * Updates an item in the list (for update operations)
 *
 * @param items - Current array of items
 * @param id - ID of item to update
 * @param updates - Partial updates to apply
 * @returns Updated array with modified item
 */
export const updateItem = <T extends BaseEntity>(
  items: T[],
  id: string,
  updates: Partial<T>
): T[] => {
  return items.map((item) =>
    item.id === id
      ? {
          ...item,
          ...updates,
          updatedAt: new Date().toISOString(),
        }
      : item
  );
};

/**
 * Creates onMutate handler for CREATE mutations
 *
 * This function:
 * 1. Cancels ongoing queries to prevent race conditions
 * 2. Snapshots current data for rollback
 * 3. Optimistically adds the new item to cache
 * 4. Returns context for error rollback
 *
 * @param queryClient - React Query client instance
 * @param queryKey - Query key to update
 * @param optimisticItem - Item to add optimistically
 * @returns Context with previous data for rollback
 */
export const onMutateCreate = async <T extends BaseEntity>(
  queryClient: QueryClient,
  queryKey: readonly unknown[] | unknown[],
  optimisticItem: T
): Promise<MutationContext<T[]>> => {
  // Cancel any outgoing refetches to prevent race conditions
  await queryClient.cancelQueries({ queryKey: queryKey as unknown[] });

  // Snapshot the previous value for rollback
  const previousData = queryClient.getQueryData<T[]>(queryKey as unknown[]);

  // Optimistically update to the new value
  queryClient.setQueryData<T[]>(queryKey as unknown[], (old = []) => [
    ...old,
    optimisticItem,
  ]);

  // Return context with snapshot
  return { previousData };
};

/**
 * Creates onMutate handler for UPDATE mutations
 *
 * This function:
 * 1. Cancels ongoing queries to prevent race conditions
 * 2. Snapshots current data for rollback
 * 3. Optimistically updates the item in cache
 * 4. Returns context for error rollback
 *
 * @param queryClient - React Query client instance
 * @param queryKey - Query key to update
 * @param id - ID of item to update
 * @param updates - Partial updates to apply
 * @returns Context with previous data for rollback
 */
export const onMutateUpdate = async <T extends BaseEntity>(
  queryClient: QueryClient,
  queryKey: readonly unknown[] | unknown[],
  id: string,
  updates: Partial<T>
): Promise<MutationContext<T[]>> => {
  await queryClient.cancelQueries({ queryKey: queryKey as unknown[] });

  const previousData = queryClient.getQueryData<T[]>(queryKey as unknown[]);

  queryClient.setQueryData<T[]>(queryKey as unknown[], (old = []) =>
    updateItem(old, id, updates)
  );

  return { previousData };
};

/**
 * Creates onMutate handler for DELETE mutations
 *
 * This function:
 * 1. Cancels ongoing queries to prevent race conditions
 * 2. Snapshots current data for rollback
 * 3. Optimistically removes the item from cache
 * 4. Returns context for error rollback
 *
 * @param queryClient - React Query client instance
 * @param queryKey - Query key to update
 * @param id - ID of item to delete
 * @returns Context with previous data for rollback
 */
export const onMutateDelete = async <T extends BaseEntity>(
  queryClient: QueryClient,
  queryKey: readonly unknown[] | unknown[],
  id: string
): Promise<MutationContext<T[]>> => {
  await queryClient.cancelQueries({ queryKey: queryKey as unknown[] });

  const previousData = queryClient.getQueryData<T[]>(queryKey as unknown[]);

  queryClient.setQueryData<T[]>(queryKey as unknown[], (old = []) =>
    removeItem(old, id)
  );

  return { previousData };
};

/**
 * Generic onError handler for rollback
 *
 * Restores the previous data snapshot when mutation fails
 *
 * @param queryClient - React Query client instance
 * @param queryKey - Query key to restore
 * @param context - Context with previous data snapshot
 */
export const onError = <T>(
  queryClient: QueryClient,
  queryKey: readonly unknown[] | unknown[],
  context: MutationContext<T> | undefined
): void => {
  if (context?.previousData) {
    queryClient.setQueryData(queryKey as unknown[], context.previousData);
  }
};

/**
 * Generic onSuccess handler for CREATE mutations
 *
 * Replaces the optimistic item with real server data
 *
 * @param queryClient - React Query client instance
 * @param queryKey - Query key to update
 * @param data - Real data from server
 */
export const onSuccessCreate = <T extends BaseEntity>(
  queryClient: QueryClient,
  queryKey: readonly unknown[] | unknown[],
  data: T
): void => {
  queryClient.setQueryData<T[]>(queryKey as unknown[], (old = []) =>
    replaceOptimisticItem(old, data)
  );
};

/**
 * Generic onSettled handler
 *
 * Always refetch after mutation settles (success or error)
 * This ensures cache is in sync with server
 *
 * @param queryClient - React Query client instance
 * @param invalidateKey - Query key pattern to invalidate
 */
export const onSettled = (
  queryClient: QueryClient,
  invalidateKey: readonly unknown[] | unknown[]
): void => {
  queryClient.invalidateQueries({ queryKey: invalidateKey as unknown[] });
};

/**
 * Creates a complete optimistic mutation configuration
 *
 * This is the high-level helper that combines all the individual handlers
 * into a ready-to-use mutation config object.
 *
 * @example
 * ```typescript
 * const createMutation = useMutation(
 *   createOptimisticMutation({
 *     queryClient,
 *     queryKey: queryKeys.tags.list(),
 *     invalidateKey: queryKeys.tags.all,
 *     mutationFn: tagsService.create,
 *     type: 'create',
 *     createOptimisticItem: (data) => ({
 *       ...data,
 *       id: createTempId(),
 *       createdAt: new Date().toISOString(),
 *       updatedAt: new Date().toISOString(),
 *     }),
 *   })
 * );
 * ```
 */
export const createOptimisticMutation = <TData extends BaseEntity, TVariables>({
  queryClient,
  queryKey,
  invalidateKey,
  mutationFn,
  type,
  createOptimisticItem: createOptimistic,
  getId,
  getUpdates,
}: {
  queryClient: QueryClient;
  queryKey: unknown[];
  invalidateKey: unknown[];
  mutationFn: (variables: TVariables) => Promise<TData>;
  type: 'create' | 'update' | 'delete';
  createOptimisticItem?: (variables: TVariables) => TData;
  getId?: (variables: TVariables) => string;
  getUpdates?: (variables: TVariables) => Partial<TData>;
}) => {
  return {
    mutationFn,

    onMutate: async (variables: TVariables) => {
      switch (type) {
        case 'create': {
          if (!createOptimistic) {
            throw new Error('createOptimisticItem is required for create type');
          }
          const optimisticItem = createOptimistic(variables);
          return onMutateCreate(queryClient, queryKey, optimisticItem);
        }

        case 'update': {
          if (!getId || !getUpdates) {
            throw new Error(
              'getId and getUpdates are required for update type'
            );
          }
          const id = getId(variables);
          const updates = getUpdates(variables);
          return onMutateUpdate(queryClient, queryKey, id, updates);
        }

        case 'delete': {
          if (!getId) {
            throw new Error('getId is required for delete type');
          }
          const id = getId(variables);
          return onMutateDelete(queryClient, queryKey, id);
        }

        default:
          throw new Error(`Unknown mutation type: ${type}`);
      }
    },

    onError: (
      _err: unknown,
      _variables: TVariables,
      context: MutationContext<TData[]> | undefined
    ) => {
      onError(queryClient, queryKey, context);
    },

    onSuccess: (data: TData) => {
      if (type === 'create') {
        onSuccessCreate(queryClient, queryKey, data);
      }
      // Update and delete don't need special onSuccess handling
      // The optimistic update is already correct
    },

    onSettled: () => {
      onSettled(queryClient, invalidateKey);
    },
  };
};

/**
 * Type guard to check if an item has a temporary ID
 *
 * @param item - Item to check
 * @returns True if item has temporary ID
 */
export const isTempId = (item: BaseEntity): boolean => {
  return item.id.startsWith('temp-');
};

/**
 * Filters out items with temporary IDs
 *
 * Useful for displaying only real items (hiding optimistic ones)
 *
 * @param items - Array of items
 * @returns Array without temporary items
 */
export const filterOutTempItems = <T extends BaseEntity>(items: T[]): T[] => {
  return items.filter((item) => !isTempId(item));
};

/**
 * Gets only items with temporary IDs
 *
 * Useful for displaying loading states or special styling for optimistic items
 *
 * @param items - Array of items
 * @returns Array of only temporary items
 */
export const getTempItems = <T extends BaseEntity>(items: T[]): T[] => {
  return items.filter((item) => isTempId(item));
};
